import { useCallback, useContext, useMemo, useState } from "react";
import { SvgIconComponent } from "@mui/icons-material";
import ChairIcon from "@mui/icons-material/Chair";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Grid2 as Grid,
  Stack,
  SvgIconProps,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import { AppContext } from "../App";
import { CycleIcon } from "../components/CycleIcon";
import { fetchPlaceSearch, PlaceSuggestion } from "../utils/fetchPlaceSearch";
import { fetchReverseGeocoding } from "../utils/fetchReverseGeocoding";
import { ImageButton } from "../components/ImageButton";
import { usePostHog } from "@posthog/react";

interface DeliveryOption {
  label: string;
  Icon: SvgIconComponent | React.FC<SvgIconProps>;
  subText: string;
}

// Constants
const DELIVERY_OPTIONS: DeliveryOption[] = [
  { label: "In-person", Icon: ChairIcon, subText: "In-person delivery only" },
  { label: "Remote", Icon: SmartphoneIcon, subText: "Remote delivery only" },
  { label: "Hybrid", Icon: CycleIcon, subText: "A mix of in-person and remote delivery" },
  { label: "No Preference", Icon: SentimentNeutralIcon, subText: "I am unsure" },
] as const;

const DEBOUNCE_DELAY = 350;

export function ProgramDeliveryPreference() {
  const { updateForm, form, handleNewError } = useContext(AppContext);
  const posthog = usePostHog();
  const [address, setAddress] = useState(form.formattedAddress);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);

  const options = useMemo(() => suggestions.map((s) => s.text), [suggestions]);

  const handleSubmit = useCallback(
    (value: string) => {
      updateForm({ providerDeliveryOptions: value });
      posthog?.capture("delivery_preference_selected", { delivery_option: value });
    },
    [updateForm, posthog]
  );

  const fetchSuggestions = useCallback(
    async (value: string) => {
      setLoading(true);
      const bias =
        typeof form.location?.lat === "number" && typeof form.location?.lng === "number"
          ? { lat: form.location.lat as number, lng: form.location.lng as number }
          : undefined;
      try {
        const res = await fetchPlaceSearch(value, bias);
        setSuggestions(res);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        handleNewError("Failed to fetch location suggestions. Please try again later.");
      } finally {
        setLoading(false);
      }
    },
    [form.location]
  );

  const debouncedFetchSuggestions = useMemo(() => debounce(fetchSuggestions, DEBOUNCE_DELAY), [fetchSuggestions]);

  const handleAddressChange = useCallback(
    (_event: React.SyntheticEvent, value: string, reason: string) => {
      setAddress(value);
      if (value && reason === "input") {
        // Only fetch for user input
        debouncedFetchSuggestions(value);
      } else {
        setSuggestions([]);
      }
    },
    [debouncedFetchSuggestions]
  );

  const handleAddressSelect = useCallback(
    (_event: React.SyntheticEvent, value: string | null) => {
      if (!value) return;

      const selectedSuggestion = suggestions.find((s) => s.text === value);
      if (!selectedSuggestion) return;

      updateForm({
        formattedAddress: value,
        country: selectedSuggestion.address.country,
        state: selectedSuggestion.address.state,
        city: selectedSuggestion.address.city,
        placeId: selectedSuggestion.placeId,
        location: selectedSuggestion.location,
      });
      posthog?.capture("location_selected", {
        city: selectedSuggestion.address.city,
        state: selectedSuggestion.address.state,
        country: selectedSuggestion.address.country,
      });
    },
    [suggestions, updateForm, posthog]
  );

  const getCurrentPosition = useCallback((): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      });
    });
  }, []);

  const handleGetCurrentLocation = useCallback(async () => {
    setLoading(true);
    posthog?.capture("current_location_used");
    try {
      const position = await getCurrentPosition();
      const locationData = await fetchReverseGeocoding(position.coords);

      if (locationData) {
        setAddress(locationData.formattedAddress);
        updateForm({
          formattedAddress: locationData.formattedAddress,
          placeId: locationData.placeId,
          location: locationData.location,
          city: locationData.city,
          state: locationData.state,
          country: locationData.country,
        });
      }
    } catch (err: Error | any | unknown) {
      console.error("Error getting current location:", err);
      handleNewError(
        "Unable to detect your current location. Please make sure:\n" +
          " - Location services are enabled on your device\n" +
          " - You've granted location permission to this site\n"
      );
    } finally {
      setLoading(false);
    }
  }, [getCurrentPosition, updateForm, posthog]);

  const showHelperText = suggestions.length && !form.formattedAddress;

  return (
    <Stack direction="column" spacing={2} alignItems="center">
      <Typography variant="h6">My preference for program delivery is:</Typography>
      <Grid container columns={{ xs: 1, sm: 2, md: 4 }} justifyContent="center" spacing={2}>
        {DELIVERY_OPTIONS.map(({ label, Icon, subText }) => (
          <Grid
            key={label}
            size={1}
            sx={{
              "& > *": {
                width: "100%",
                height: "100%",
              },
            }}
          >
            <ImageButton
              label={label}
              image={<Icon />}
              onClick={() => handleSubmit(label)}
              subText={subText}
              selected={form.providerDeliveryOptions === label}
            />
          </Grid>
        ))}
      </Grid>

      {form.providerDeliveryOptions && (
        <Stack direction="column" spacing={2} paddingTop={2} alignItems="center">
          <Typography variant="h6">Please enter your city as some providers have geographical restrictions:</Typography>
          <Autocomplete
            freeSolo
            sx={{ width: 300 }}
            value={address}
            onChange={handleAddressSelect}
            onInputChange={handleAddressChange}
            filterOptions={(x) => x}
            loading={loading}
            options={options}
            noOptionsText="No locations found"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Please enter your city"
                fullWidth
                helperText={showHelperText ? "Please select an option from the dropdown" : ""}
              />
            )}
            getOptionLabel={(option: string) => option}
          />

          <Typography>OR</Typography>

          <Button
            variant="outlined"
            onClick={handleGetCurrentLocation}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            aria-label="Get current location"
            sx={{
              maxWidth: "260px",
              minWidth: "200px",
              position: "relative",
              "&.Mui-disabled": {
                cursor: "not-allowed",
              },
            }}
          >
            {loading ? "GETTING LOCATION..." : "USE MY CURRENT LOCATION TO DETERMINE MY CITY"}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
